/**
 * @nostr-post/plugin-wiki-entity - Core
 *
 * Plugin definition for the wiki-entity-picker field type.
 * Used in review manifests to select an entity and emit its `a` + `i` tags.
 *
 * No DOM dependencies — safe for SSR/Node.
 */

import type { NostrPostManifest } from '@nostr-post/core/types';
import type { NostrUIPlugin, PostField, Result, ValidationError } from '@nostr-post/plugins/types';
import {
  type EntityTypeAffixes,
  extractExternalIds,
  getEntityManifest,
  normalizeDTag,
  type WikiEvent,
} from '@nostr-post/wiki';
import { entityItems, entityJsonSchema } from './entityJson';

/**
 * Data shape stored for a wiki-entity-picker field.
 * This is what the coordinator sees in formData[fieldId].
 */
export interface WikiEntityData {
  /** NIP-54 d-tag of the selected entity (e.g. "pliny-the-elder") */
  dTag: string;
  /** Pubkey of the canonical (resolved) version of the entity */
  resolvedPubkey: string;
  /** All `i` tag values copied from the entity at selection time */
  externalIds: string[];
  /** Human-readable display name */
  displayName?: string;
  /**
   * An entity without a page (yet): `dTag` and `resolvedPubkey` are empty, no `a` tag is written,
   * its links still are (`externalIds` as `i` tags). E.g. a performer read from a post.
   */
  unlinked?: true;
  /** For an unlinked entity: form data to create its page with (its name and links) */
  prefill?: Record<string, unknown>;
  /**
   * For an unlinked entity: create its page when the post is published (`createsPage`). Unset
   * means yes when it has links, no for a name alone (namesakes)
   */
  createPage?: boolean;
  /**
   * A page found for the entity automatically (`resolveEntities`), not confirmed by the user yet:
   * found `by` its links or its name; `instead`, the unlinked value it was ("Not them")
   */
  match?: { by: 'link' | 'name'; instead: WikiEntityData };
}

/**
 * Metadata config available in the manifest field.
 * { "id": "beer", "uiPlugin": "wiki-entity-picker",
 *   "metadata": { "entityManifest": "beer-entity-v1" } }
 */
export interface WikiEntityPickerConfig {
  /**
   * The manifest that defines the entity type being picked: a published
   * manifest's address (`30078:<pubkey>:nostr-post:<id>`, fetched from relays),
   * an id registered with registerEntityManifest, or the manifest itself.
   * Scopes search to that type and enables "+ Create".
   */
  entityManifest?: string | NostrPostManifest;
  /** Relays to search for entities */
  relays?: string[];
  /** Minimum characters before triggering a search */
  minSearchLength?: number;
  /**
   * Set to `false` to suppress `extraTags` emission for this field.
   * Useful when referencing an entity from another entity manifest to avoid
   * copying the referenced entity's `i` tags into the referencing event.
   * Defaults to `true` (extra tags are emitted).
   */
  emitExtraTags?: boolean;
  /** Pick several entities (the value is an array), e.g. the breweries of a collaboration beer */
  multiple?: boolean;
}

const isMultiple = (field: PostField): boolean =>
  (field.metadata as WikiEntityPickerConfig | undefined)?.multiple === true;

/** A picked entity's value from its (resolved) wiki event */
export const entityDataOf = (event: WikiEvent): WikiEntityData => {
  const dTag = event.tags.find((t) => t[0] === 'd')?.[1] ?? '';
  return {
    dTag,
    resolvedPubkey: event.pubkey,
    externalIds: extractExternalIds(event),
    displayName: event.tags.find((t) => t[0] === 'title')?.[1] ?? dTag,
  };
};

/** The picked entities as a list, whether the field holds one or several */
export const pickedEntities = (value: unknown): WikiEntityData[] =>
  (Array.isArray(value) ? value : [value]).filter(
    (v): v is WikiEntityData => !!v && typeof v === 'object' && !Array.isArray(v)
  );

// The registry lives in @nostr-post/wiki so other plugins (e.g. venue) share it
export { getEntityManifest, registerEntityManifest } from '@nostr-post/wiki';

/**
 * Form data that puts `name` into the entity's name field: the first
 * `{placeholder}` of wikiConfig.titleTemplate, else the first required string field.
 */
export function entityPrefill(manifest: NostrPostManifest, name: string): Record<string, unknown> {
  const fieldId = entityNameField(manifest);
  return fieldId ? { [fieldId]: name } : {};
}

/** The field holding an entity's name: the title template's first, else the first required text */
export function entityNameField(manifest: NostrPostManifest): string | undefined {
  const titleTemplate = (manifest as { wikiConfig?: { titleTemplate?: string } }).wikiConfig
    ?.titleTemplate;
  const fromTemplate = titleTemplate?.match(/\{(\w+)\}/)?.[1];
  return fromTemplate ?? manifest.fields.find((f) => f.required && f.type === 'string')?.id;
}

// Entity type affixes live in @nostr-post/wiki so other plugins (e.g. venue) share them
export { type EntityTypeAffixes, entityTypeAffixes } from '@nostr-post/wiki';

/** Whether `dTag` belongs to the entity type (and has a name between the affixes). */
export function matchesEntityType(dTag: string, { prefix, suffix }: EntityTypeAffixes): boolean {
  return (
    dTag.length > prefix.length + suffix.length && dTag.startsWith(prefix) && dTag.endsWith(suffix)
  );
}

/** The d-tag an entity of this type named `name` gets: `bitcoin` → `bitcoin-beer`. */
export function entityTypeDTag(name: string, { prefix, suffix }: EntityTypeAffixes): string {
  const slug = normalizeDTag(name);
  return slug ? `${prefix}${slug}${suffix}` : '';
}

/** Lines that are markup rather than text: tables, rules, code fences, link-only lines */
const NON_TEXT_LINE = /^(\||[-=*_]{3,}|```|\[\[?[^\]]*\]\]?$)/;

/**
 * The d-tag "+ Create" would publish for `query`, and whether an entity with it
 * already exists (creating would then only add a version of that entity).
 */
export function createTarget(
  query: string,
  existingDTags: string[],
  affixes: EntityTypeAffixes
): { dTag: string; exists: boolean } {
  const dTag = entityTypeDTag(query, affixes);
  return { dTag, exists: existingDTags.includes(dTag) };
}

/**
 * A short plain-text description of an entity: its `summary` tag, else the
 * first line of text in the content, without markdown markup.
 */
export function entitySnippet(tags: string[][], content: string, maxLength = 120): string {
  const summary = tags.find((t) => t[0] === 'summary')?.[1]?.trim();
  const line =
    summary ||
    content
      .split('\n')
      .map((l) => l.trim())
      .find((l) => /\p{L}/u.test(l) && !NON_TEXT_LINE.test(l)) ||
    '';
  const text = line
    .replace(/^[#>*\-+\s]+/, '')
    .replace(/\[\[([^\]|]+)(\|[^\]]*)?\]\]/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/[*_`]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > maxLength ? `${text.slice(0, maxLength - 1).trimEnd()}…` : text;
}

/** Parses a `30818:<pubkey>:<d-tag>` address into a minimal entity value. */
function parseWikiAddress(raw: string): WikiEntityData | undefined {
  const parts = raw.split(':');
  if (parts.length < 3 || parts[0] !== '30818') {
    return undefined;
  }
  const resolvedPubkey = parts[1];
  const dTag = parts.slice(2).join(':');
  if (!resolvedPubkey || !dTag) {
    return undefined;
  }
  return { dTag, resolvedPubkey, externalIds: [] };
}

/** Why a picked entity is incomplete, as a validation failure */
function entityProblem(
  entity: Record<string, unknown>,
  fieldId: string
): Result<void, ValidationError> | undefined {
  const fail = (message: string): Result<void, ValidationError> => ({
    success: false,
    error: { field: fieldId, message, code: 'INVALID_VALUE' },
  });
  if (entity.unlinked === true) {
    const named = typeof entity.displayName === 'string' && entity.displayName.length > 0;
    return named || Array.isArray(entity.externalIds) ? undefined : fail('Entity has no name');
  }
  if (typeof entity.dTag !== 'string' || entity.dTag.length === 0) {
    return fail('Selected entity is missing a d-tag');
  }
  if (typeof entity.resolvedPubkey !== 'string' || entity.resolvedPubkey.length === 0) {
    return fail('Selected entity is missing a resolved pubkey');
  }
  return undefined;
}

export const wikiEntityPickerPlugin: NostrUIPlugin = {
  id: 'wiki-entity-picker',
  type: 'ref',

  validate: (value: unknown, field: PostField): Result<void, ValidationError> => {
    const empty =
      value === undefined || value === null || (Array.isArray(value) && value.length === 0);
    if (empty) {
      if (field.required) {
        return {
          success: false,
          error: {
            field: field.id,
            message: 'Please select an entity',
            code: 'REQUIRED',
          },
        };
      }
      return { success: true, data: undefined };
    }

    if (typeof value !== 'object' || (Array.isArray(value) && !isMultiple(field))) {
      return {
        success: false,
        error: {
          field: field.id,
          message: 'Entity picker value must be an object',
          code: 'INVALID_TYPE',
        },
      };
    }

    for (const entity of (Array.isArray(value) ? value : [value]) as Record<string, unknown>[]) {
      const problem = entityProblem(entity, field.id);
      if (problem) return problem;
    }
    return { success: true, data: undefined };
  },

  /**
   * Serialises the field value into the canonical string that becomes the tag
   * value. For wiki-entity-picker this is the NIP-54 `a`-tag address:
   *   "30818:<resolvedPubkey>:<dTag>"
   *
   * This is what the coordinator writes as the primary tag value (combined
   * with `mapTo.tagName: 'a'`). `extraTags` is then reserved solely for
   * supplemental `i` tags copied from the entity.
   */
  serializeValue: (value: unknown): string => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return '';
    const entity = value as WikiEntityData;
    if (entity.unlinked) return '';
    return `30818:${entity.resolvedPubkey}:${entity.dTag}`;
  },

  /**
   * Deserialises the canonical NIP-54 `a`-tag address back into the minimal
   * structured value expected by the picker for prefill/edit flows.
   */
  deserializeValue: (raw: string, _field: PostField): WikiEntityData | undefined =>
    parseWikiAddress(raw),

  /**
   * Rebuilds the full entity value from an event's tags for view/edit flows.
   * Unlike `deserializeValue` (single tag value only), this also restores the
   * `i` tags copied from the entity, so re-publishing an edit keeps them.
   */
  resolveFromTags: (
    tags: string[][],
    field: PostField
  ): WikiEntityData | WikiEntityData[] | undefined => {
    const targets = Array.isArray(field.mapTo) ? field.mapTo : [field.mapTo];
    const tagName = targets.find((t) => t.target === 'tag' && t.tagName)?.tagName ?? 'a';
    const entities = tags
      .filter((t) => t[0] === tagName && t[1]?.startsWith('30818:'))
      .map((t) => parseWikiAddress(t[1] as string))
      .filter((e): e is WikiEntityData => !!e);
    const ids = tags.filter((t) => t[0] === 'i' && t[1]).map((t) => t[1] as string);
    if (isMultiple(field)) {
      // The `i` tags can't be told apart by entity: kept on a nameless unlinked value, so editing
      // the event keeps the links of picked and unlinked entities alike (`withPageLinks` gives
      // picked entities theirs back)
      const links: WikiEntityData[] = ids.length
        ? [{ dTag: '', resolvedPubkey: '', externalIds: ids, unlinked: true }]
        : [];
      const all = [...entities, ...links];
      return all.length ? all : undefined;
    }
    if (entities.length === 0) return undefined;
    const [entity] = entities as [WikiEntityData];
    const config = field.metadata as WikiEntityPickerConfig | undefined;
    if (config?.emitExtraTags !== false) {
      entity.externalIds = tags.filter((t) => t[0] === 'i' && t[1]).map((t) => t[1]);
    }
    return entity;
  },

  /**
   * In form JSON: the entities by name and links, as the entity manifest's name and link fields
   * ask (`entityJsonSchema`), or by name when the entity manifest isn't known yet
   */
  jsonSchema: (field: PostField) => {
    const manifest = getEntityManifest((field.metadata as WikiEntityPickerConfig)?.entityManifest);
    const item = manifest
      ? entityJsonSchema(manifest)
      : { type: 'string', description: 'A name, as written' };
    return isMultiple(field) ? { type: 'array', items: item } : item;
  },

  /**
   * Creates the pages of unlinked entities the user left ticked (`createEntityPage`), so the
   * post links them; runs per entity of a list. Loaded lazily, like the venue's
   */
  beforePublish: async (value: unknown, field: PostField) => {
    const { createEntityPage } = await import('./entityPage');
    return createEntityPage(value, field);
  },

  /** The entities' pages, found by link or name (`resolveEntities`); unlinked values for the others */
  parseJson: async (value: unknown, field: PostField) => {
    const config = (field.metadata as WikiEntityPickerConfig | undefined) ?? {};
    const manifest = getEntityManifest(config.entityManifest);
    const { resolveEntities } = await import('./resolveEntities');
    const { entities, suggestions } = await resolveEntities(entityItems(value, manifest), config);
    const picked = isMultiple(field) ? entities : entities[0];
    return { value: entities.length ? picked : undefined, suggestions };
  },

  /**
   * extraTags implementation.
   *
   * Emits only the `i` tags copied from the entity at selection time for
   * cross-platform discovery. The `a` tag is handled by `serializeValue`
   * together with the field's `mapTo.tagName: 'a'` — no duplication.
   */
  extraTags: (value: unknown, field: PostField): [string, ...string[]][] => {
    const config = field.metadata as WikiEntityPickerConfig | undefined;
    if (config?.emitExtraTags === false) return [];
    const ids = pickedEntities(value).flatMap((e) =>
      Array.isArray(e.externalIds) ? e.externalIds : []
    );
    return [...new Set(ids)].map((id): [string, string] => ['i', id]);
  },
};

/**
 * Client-side relevance check for search results: true when the entity's
 * title or d-tag contains the query (case-insensitive, also comparing the
 * normalized slug form so "pliny the" matches "pliny-the-elder").
 */
export function matchesEntityQuery(tags: string[][], query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return false;
  const slug = normalizeDTag(q);
  const dTag = tags.find((t) => t[0] === 'd')?.[1]?.toLowerCase() ?? '';
  const title = tags.find((t) => t[0] === 'title')?.[1]?.toLowerCase() ?? '';
  return title.includes(q) || dTag.includes(q) || (slug !== '' && dTag.includes(slug));
}
